import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { MultipleDocumentUploader } from "@/common/components/MultipleDocumentUploader";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { useMaintenanceFiscalAssessmentDocumentGroupsUpdateDocumentsAppend } from "@/features/maintenance/features/workflows/hooks/forwardings/fiscalAssessmentDocumentGroups/useMaintenanceFiscalAssessmentDocumentGroupsUpdateDocumentsAppend";
import { fiscalAssessmentDocumentGroupsUpdateSchema, FiscalAssessmentDocumentGroupsUpdateValidationSchema } from "@/features/maintenance/features/workflows/schemas/forwardings/fiscalAssessmentDocumentGroups/fiscalAsssessmentDocumentGroupsUpdateDocuments.schema";
import { MaintenanceFiscalAssessmentDocumentGroupData } from "@/features/maintenance/features/workflows/types/forwardings/fiscalAssessmentEntry/fiscalAssessmentEntry.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface FiscalAssessmentEntryUpdateDocumentsProps {
  documentGroup: MaintenanceFiscalAssessmentDocumentGroupData;
  disabled: boolean;
}

const FiscalAssessmentEntryUpdateDocumentsAppend = ({
  documentGroup,
  disabled,
}: FiscalAssessmentEntryUpdateDocumentsProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient();

  const {
    mutateAsync: uploadBlob,
    isPending: uploadBlobIsPending,
  } = useBlobs();

  const {
    control,
    reset,
    handleSubmit,
  } = useForm<FiscalAssessmentDocumentGroupsUpdateValidationSchema>({
    resolver: zodResolver(fiscalAssessmentDocumentGroupsUpdateSchema),
    defaultValues: {
      documents_attributes: [],
    },
  });

  const {
    mutate: updateDocuments,
    isPending: updateDocumentsIsPending,
  } = useMaintenanceFiscalAssessmentDocumentGroupsUpdateDocumentsAppend({
    documentGroup,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding'],
        });

        setIsOpen(false);
      },
    },
  });

  const onSubmit: SubmitHandler<FiscalAssessmentDocumentGroupsUpdateValidationSchema> = (data) => updateDocuments(data);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="success"
            operationKlassToCheckPermission="Establishment::Maintenance::FiscalAssessmentDocumentGroups::UpdateDocuments"
            disabled={disabled}
          >
            Adicionar nota
          </Button>
        )}
        title="Enviar notas fiscais"
        useCancellableFooter
        confirmButtonClassName="bg-primary-blue"
        cancelButtonClassName="bg-white-200"
        bodyClassName="text-white-800 max-h-[40rem] overflow-auto"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonDisabled={uploadBlobIsPending}
        confirmButtonIsLoading={updateDocumentsIsPending}
        body={(
          <MultipleDocumentUploader
            control={control}
            uploadBlob={uploadBlob}
            fieldName="documents_attributes"
            className="bg-white-200 border-white-200 w-full"
            fileItemClassName="w-full"
            fileListClassName="flex flex-col"
            containerClassName="flex flex-col"
            title="Anexe as notas fiscais"
            accept="image/png, image/jpg, image/webp, .pdf, .xml"
            maxFileSizeInMb={20}
            isLoading={uploadBlobIsPending}
            disabled={uploadBlobIsPending}
            fieldKey="blob_id"
            setError={(error) => {
              if (error) {
                setMessages([error]);
              }
            }}
          />
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default FiscalAssessmentEntryUpdateDocumentsAppend;
